import { Component, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { DevManagementDataService } from "@app/admin/services/devmanagement.service";
import {
    RoleMaintenanceModel,
    RightMaintenanceModel,
} from "@app/admin/shared/RoleRightMaintenanceModel";
import { ToastrService } from "ngx-toastr";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { DevRoleRightPatchesComponent } from "./dev-role-right-patches/dev-role-right-patches.component";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { InputTooltipDirective } from "@app/shared/directives/input-tooltip.directive";
import { firstValueFrom } from "rxjs";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/admin/i18n/scope";
import { scope as rootScope } from "@app/shared/i18n/scope";

@Component({
    selector: "dev-cerrixrole",
    templateUrl: "./dev-cerrixrole.component.html",
    styleUrls: ["./dev-cerrixrole.component.scss"],
    imports: [FormsModule, NgSelectModule, InputTooltipDirective, TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class DevCerrixroleComponent {
    private _ds = inject(DevManagementDataService);
    private _toastr = inject(ToastrService);
    private _modalService = inject(BsModalService);
    private _promptService = inject(CerrixPromptService);
    private translation = injectTranslation(scope);

    rootScope = rootScope;

    roles: RoleMaintenanceModel[];
    selectedRole: RoleMaintenanceModel;
    selectedRight: RightMaintenanceModel;

    bsModalRef: BsModalRef;

    constructor() {
        this.loadRoles();
    }

    loadRoles() {
        this._ds.getRoleRightDefinitions().subscribe((x) => {
            this.roles = x;
            this.selectedRole = this.roles[0];
            this.setRight();
        });
    }

    setRight() {
        this.selectedRight = this.selectedRole.rights[0];
    }

    changeLink() {
        const call =
            this.selectedRight.currentValue === this.selectedRight.defaultValue
                ? this._ds.setRoleRightDefinition(
                      this.selectedRole.roleID,
                      this.selectedRight.rightID
                  )
                : this._ds.deleteRoleRightDefinition(
                      this.selectedRole.roleID,
                      this.selectedRight.rightID
                  );

        call.subscribe(() => {
            this.selectedRight.currentValue = !this.selectedRight.currentValue;
        }, this.throwUpdateError);
    }

    async deleteLink(onlySelectedRole: boolean) {
        await this.translation.ready();
        let roleID = null;
        if (onlySelectedRole) {
            roleID = this.selectedRole.roleID;
            if (!roleID || roleID <= 0) {
                this._toastr.error(
                    this.translation.t("devCerrixrole.selectedRoleNotValid"),
                    this.translation.t("devCerrixrole.couldNotDeleteLinks")
                );
                return;
            }
        }

        const confirmMessage = this.translation.t("devCerrixrole.removePatchesConfirmMessage", {
            target: onlySelectedRole ? "role" : "all",
            roleName: this.selectedRole?.roleName,
        });

        if (
            await this._promptService
                .confirm(this.translation.t("devCerrixrole.confirm"), confirmMessage)
                .toPromise()
        ) {
            if (
                await this._promptService
                    .confirm(
                        this.translation.t("devCerrixrole.confirm"),
                        this.translation.t("devCerrixrole.removePatchesFinalConfirmMessage")
                    )
                    .toPromise()
            ) {
                await firstValueFrom(this._ds.deleteRoleRightDefinition(roleID));
                this.loadRoles();
                this._promptService.alert({
                    data: {
                        title: this.translation.t("devCerrixrole.done"),
                        message: this.translation.t("devCerrixrole.roleRightDefinitionDeleted"),
                    },
                });
            }
        }
    }

    openPatchesModal() {
        this.bsModalRef = this._modalService.show(DevRoleRightPatchesComponent, {
            class: "modal-xl",
        });
    }

    async throwUpdateError() {
        await this.translation.ready();
        this._toastr.error(
            this.translation.t("devCerrixrole.updateLinkFailedMessage"),
            this.translation.t("devCerrixrole.updateFailed")
        );
    }

    customSearchRoleFn(term: string, item: RoleMaintenanceModel) {
        term = term.toLowerCase();
        return (
            item.roleName.toLowerCase().indexOf(term) > -1 ||
            item.roleDescription.toLowerCase().indexOf(term) > -1
        );
    }

    customSearchRightFn(term: string, item: RightMaintenanceModel) {
        term = term.toLowerCase();
        return (
            item.rightName.toLowerCase().indexOf(term) > -1 ||
            item.rightDescription.toLowerCase().indexOf(term) > -1
        );
    }
}
